// src/context/UserProvider.js
import React, { useState, useEffect, useContext, useRef } from "react";
import api, { apiEvents } from "../../api/axios";
import Swal from "sweetalert2";
import { UserContext } from "./Context";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import LoadingMainPage from "../Common/Loading/LoadingMainPage";
import {
  buildClientSignPath,
  normalizeExpedienteId,
} from "../../utils/expediente";

const DEFAULT_HEARTBEAT_MS = 30000;
const MIN_HEARTBEAT_MS = 15000;

const parsedHeartbeatMs = Number(process.env.REACT_APP_SESSION_HEARTBEAT_MS);
const SESSION_HEARTBEAT_INTERVAL_MS = Number.isFinite(parsedHeartbeatMs)
  ? Math.max(parsedHeartbeatMs, MIN_HEARTBEAT_MS)
  : DEFAULT_HEARTBEAT_MS;

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState({ user: null, userCompany: null });
  const [companyImage, setCompanyImage] = useState(null);
  const [companyImageRealTime, setCompanyImageRealTime] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSessionRecoveryPending, setIsSessionRecoveryPending] =
    useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isLoginTransitionLoading, setIsLoginTransitionLoading] =
    useState(false);
  const [isExpedienteAccessChoicePending, setIsExpedienteAccessChoicePending] =
    useState(false);
  const isRefreshingSessionRef = useRef(false);
  const isSessionPromptOpenRef = useRef(false);
  const promptSessionRenewalRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const isHeartbeatInFlightRef = useRef(false); // evita heartbeats concurrentes
  const heartbeatDebounceRef = useRef(null); // debounce para focus/visibilitychange
  const expedientePromptRouteRef = useRef("");

  const location = useLocation();
  const navigate = useNavigate();

  // REF para tener siempre el valor actual dentro del handler del event emitter
  const isLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const fetchUserData = async () => {
    console.log("[UserProvider] fetchUserData -> start");
    try {
      const url = `${process.env.REACT_APP_API_URL}`;

      const response = await api.post(url + "/getuser");
      console.log("[UserProvider] /getuser OK");

      const { user, userCompany, token, isSuperAdmin } = response.data || {};

      if (user) {
        setUserData({ user, userCompany, token, isSuperAdmin });
        setIsLoggedIn(true);
      } else {
        setUserData({ user: null, userCompany: null });
        setIsLoggedIn(false);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn("[UserProvider] /getuser 401 - no autenticado");
      } else {
        console.error("[UserProvider] Error fetching user data:", error);
      }
      setUserData({ user: null, userCompany: null });
      setIsLoggedIn(false);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSession = async () => {
    if (isRefreshingSessionRef.current) {
      return {
        ok: false,
        message: "Ya hay una renovación de sesión en curso.",
      };
    }

    isRefreshingSessionRef.current = true;

    try {
      const storedToken =
        sessionStorage.getItem("token") ?? userData?.token ?? "";
      const response = await api.post(
        `/session/refresh?token=${encodeURIComponent(storedToken)}`,
        { token: storedToken },
        {
          headers: storedToken
            ? {
                Authorization: `Bearer ${storedToken}`,
              }
            : undefined,
          _skipUnauthorizedEvent: true,
        },
      );

      const refreshedToken = response?.data?.token;
      if (refreshedToken) {
        sessionStorage.setItem("token", refreshedToken);
      }

      await fetchUserData();
      return {
        ok: true,
        message: "Sesión renovada correctamente.",
      };
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      const message =
        typeof apiMessage === "string" && apiMessage.trim()
          ? apiMessage
          : "No se pudo renovar la sesión. Debes iniciar sesión nuevamente.";

      console.warn("[UserProvider] No se pudo renovar la sesión", {
        status: error?.response?.status,
        data: error?.response?.data,
      });
      return {
        ok: false,
        message,
      };
    } finally {
      isRefreshingSessionRef.current = false;
    }
  };

  const promptSessionRenewal = async () => {
    if (isSessionPromptOpenRef.current) return;

    setIsSessionRecoveryPending(true);
    isSessionPromptOpenRef.current = true;

    try {
      const result = await Swal.fire({
        icon: "warning",
        title: "Tu sesión ha caducado",
        text: "Selecciona la acción que deseas realizar.",
        showCancelButton: true,
        confirmButtonText: "Continuar sesión",
        cancelButtonText: "Cerrar sesión",
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showLoaderOnConfirm: true,
      });

      if (result.isConfirmed) {
        const refreshResult = await refreshSession();

        if (!refreshResult.ok) {
          await Swal.fire({
            icon: "error",
            title: "No se pudo renovar la sesión",
            text: refreshResult.message,
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#1ab394",
          });
          await logout(false, false);
        }

        return;
      }

      await logout(false, false);
    } finally {
      isSessionPromptOpenRef.current = false;
      setIsSessionRecoveryPending(false);
    }
  };
  promptSessionRenewalRef.current = promptSessionRenewal;

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const startLoginTransition = () => setIsLoginTransitionLoading(true);
  const finishLoginTransition = async (delayMs = 2000) => {
    await wait(delayMs);
    setIsLoginTransitionLoading(false);
  };
  const isGlobalAuthLoading =
    isLoading || isLoginTransitionLoading || isLoggingOut;
  const globalLoadingText = isLoggingOut
    ? "Cerrando sesión"
    : isLoginTransitionLoading
      ? "Iniciando sesión.."
      : "";

  // Logout unificado (manual o automático). Flag 'auto' opcional.
  const logout = async (showConfirm = false, auto = false) => {
    const doLogout = async () => {
      setIsLoggingOut(true);

      try {
        await Promise.allSettled([
          isLoggedInRef.current
            ? api.post("/logout", { scope: "user" })
            : Promise.resolve(),
          wait(2000),
        ]);

        // Solo llamar al backend si estamos realmente logueados
      } catch (err) {
        console.warn("[UserProvider] Logout backend falló", err);
      }

      if (auto) {
        await Swal.fire({
          title: "Sesión expirada",
          text: "Tu sesión ha caducado, por favor vuelve a iniciar sesión.",
          icon: "warning",
          timer: 2000,
          showConfirmButton: false,
          allowOutsideClick: false,
        });
      }

      // 🧹 Limpieza total
      sessionStorage.clear();
      localStorage.clear();

      //  Limpiar estado React SIEMPRE
      setUserData({ user: null, userCompany: null });
      setIsLoggedIn(false);
      setIsSessionRecoveryPending(false);
      setIsLoggingOut(false);

      // window.location.replace("/");
    };

    if (showConfirm) {
      const result = await Swal.fire({
        title: "Cerrar sesión",
        icon: "warning",
        html: `<b>¿Deseas continuar?</b>`,
        showCancelButton: true,
        confirmButtonText: "Sí",
        confirmButtonColor: "#1ab394",
        cancelButtonText: "No, volver",
        cancelButtonColor: "#d33",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        return doLogout();
      }
    } else {
      return doLogout();
    }
  };

  const updateCompanyImage = (image) => setCompanyImage(image);
  const updateCompanyImageRealTime = (image) => setCompanyImageRealTime(image);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (isLoading || isLoggedIn) {
      expedientePromptRouteRef.current = "";
      setIsExpedienteAccessChoicePending(false);
      return;
    }

    const isExpedienteViewRoute = location.pathname === "/expedientes/ver";
    if (!isExpedienteViewRoute) {
      expedientePromptRouteRef.current = "";
      setIsExpedienteAccessChoicePending(false);
      return;
    }

    const rawExpedienteId = new URLSearchParams(location.search).get("id");
    const expedienteId = normalizeExpedienteId(rawExpedienteId);

    if (!expedienteId) {
      expedientePromptRouteRef.current = "";
      setIsExpedienteAccessChoicePending(false);
      return;
    }

    const currentRouteKey = `${location.pathname}${location.search}`;
    if (expedientePromptRouteRef.current === currentRouteKey) {
      return;
    }

    // TEMPORALMENTE DESACTIVADO: se ha dejado comentado este aviso para evitar
    // que al entrar en /expedientes/ver?id se dispare la elección automática de acceso.
    // La lógica original permanece intacta para reactivarla cuando se resuelvan los links rotos.
    expedientePromptRouteRef.current = currentRouteKey;
    setIsExpedienteAccessChoicePending(false);
    return;

    // let cancelled = false;
    // const promptAccessChoice = async () => {
    //   const result = await Swal.fire({
    //     icon: "question",
    //     title: "Acceso al expediente",
    //     text: "Selecciona cómo deseas ingresar para continuar.",
    //     confirmButtonText: "Ingreso de clientes",
    //     confirmButtonColor: "#1ab394",
    //     showDenyButton: true,
    //     denyButtonText: "Ingreso de abogados",
    //     denyButtonColor: "#f8ac59",
    //     showCancelButton: false,
    //     allowOutsideClick: false,
    //     allowEscapeKey: false,
    //   });
    //
    //   if (!cancelled) {
    //     setIsExpedienteAccessChoicePending(false);
    //   }
    //
    //   if (result.isConfirmed) {
    //     navigate(buildClientSignPath(expedienteId), { replace: true });
    //     return;
    //   }
    //
    //   if (result.isDenied) {
    //     navigate("/", { replace: true });
    //   }
    // };
    //
    // promptAccessChoice();
    //
    // return () => {
    //   cancelled = true;
    // };
  }, [isLoading, isLoggedIn, location.pathname, location.search, navigate]);

  useEffect(() => {
    const handleUnauthorized = async () => {
      // console.log(
      //   "[UserProvider] apiEvents unauthorized received — isLoggedInRef:",
      //   isLoggedInRef.current,
      // );
      // Solo hacer logout automático si el usuario estaba realmente logueado
      if (isLoggedInRef.current) {
        await promptSessionRenewal();
      } else {
        console.log(
          "[UserProvider] Ignorado unauthorized porque el usuario no está logueado (inicio).",
        );
      }
    };

    apiEvents.on("unauthorized", handleUnauthorized);
    return () => {
      apiEvents.off("unauthorized", handleUnauthorized);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // depende solo del montaje

  useEffect(() => {
    if (!isLoggedIn) {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return undefined;
    }

    const runSessionHeartbeat = async () => {
      if (
        !isLoggedInRef.current ||
        isRefreshingSessionRef.current ||
        isSessionPromptOpenRef.current ||
        isHeartbeatInFlightRef.current // guard: no concurrent heartbeats
      ) {
        return;
      }

      isHeartbeatInFlightRef.current = true;
      try {
        await api.post("/session/heartbeat", null, {
          _skipUnauthorizedEvent: true,
        });
      } catch (error) {
        if (error?.response?.status === 401) {
          promptSessionRenewalRef.current?.();
        }
      } finally {
        isHeartbeatInFlightRef.current = false;
      }
    };

    heartbeatIntervalRef.current = setInterval(
      runSessionHeartbeat,
      SESSION_HEARTBEAT_INTERVAL_MS,
    );

    // Debounce de 400ms: focus + visibilitychange pueden disparar juntos
    // al volver a la pestaña; sólo ejecutar una vez por evento real.
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(heartbeatDebounceRef.current);
      heartbeatDebounceRef.current = setTimeout(
        () => runSessionHeartbeat(),
        400,
      );
    };

    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      clearTimeout(heartbeatDebounceRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
    };
  }, [isLoggedIn]);

  return (
    <UserContext.Provider
      value={{
        userData,
        fetchUserData,
        companyImage,
        updateCompanyImage,
        companyImageRealTime,
        updateCompanyImageRealTime,
        isLoggedIn,
        logout,
        isLoading,
        refreshSession,
        isSessionRecoveryPending,
        isExpedienteAccessChoicePending,
        startLoginTransition,
        finishLoginTransition,
        isLoginTransitionLoading,
      }}
    >
      {children ? children : <Outlet />}
      <LoadingMainPage
        isLoading={isGlobalAuthLoading}
        logoVariant="letters"
        statusText={globalLoadingText}
        minVisibleMs={4000}
      />
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
